import React, { useState, useEffect, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { imageUrl } from "../lib/format";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faNewspaper,
} from "@fortawesome/free-regular-svg-icons";
import {
  faSignOutAlt,
  faHouse,
  faSearch,
  faUser,
  faXmark,
  faBars,
  faBook,
  faFileAlt,
  faKey,
  faReceipt,
  faEnvelope,
  faBuilding,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import { useStaffAuth, useStaffContent } from "../store/staff";

const STAFF_NAV_ITEMS = [
  { title: "Dashboard", icon: faHouse, path: "/staff/dashboard" },
  { title: "News Management", icon: faNewspaper, path: "/staff/news-management" },
  { title: "Content Review", icon: faFileAlt, path: "/staff/content-review" },
  { title: "Categories", icon: faBook, path: "/staff/categories" },
  { title: "Departments", icon: faBuilding, path: "/staff/departments" },
  { title: "Marketplace", icon: faStore, path: "/staff/marketplace" },
  { title: "Notifications", icon: faBell, path: "/staff/notifications" },
  { title: "Exam PINs", icon: faKey, path: "/staff/exam-pins" },
  { title: "Exam Purchases", icon: faReceipt, path: "/staff/exam-purchases" },
  { title: "Email Deliveries", icon: faEnvelope, path: "/staff/emails" },
];

const StaffNavBar = ({ title = "Staff Dashboard", subTitle = "", context = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [uimage, setUimage] = useState("");
  const [isloaded, setIsLoaded] = useState(false);

  const navigate = useNavigate();
  const { staffLogout, fetchStaffProfile } = useStaffAuth();
  const { staffPendingCount, fetchStaffPendingCount } = useStaffContent();

  useLayoutEffect(() => {
    document.querySelectorAll("[data-staff-nav]").forEach((container) => {
      try {
        const savedPosition = sessionStorage.getItem(`staff-sidebar-scroll-${container.dataset.staffNav}`);
        if (savedPosition !== null) container.scrollTop = Number(savedPosition) || 0;
      } catch {
        // Session storage is optional; the sidebar remains scrollable.
      }
    });
  }, []);

  useEffect(() => {
    fetchStaffPendingCount();
  }, [fetchStaffPendingCount]);

  useEffect(() => {
    let active = true;

    const fetchUserDetails = async () => {
      try {
        // Profile data is loaded through the staff store (GET /api/staff/me).
        const result = await fetchStaffProfile();
        if (active && result.success && result.profile) {
          setName(result.profile.name || result.profile.firstName || "");
          setUimage(result.profile.avatar || result.profile.image || "");
        }
      } catch {
        // Silently fall back
      } finally {
        if (active) setIsLoaded(true);
      }
    };

    // `RequireAuth` restores the session before this renders, so only the header
    // details are fetched here.
    fetchUserDetails();

    return () => {
      active = false;
    };
  }, [fetchStaffProfile]);

  const handleLogout = async () => {
    // Revoke the server session (POST /api/staff/auth/logout), then clear the
    // local session and leave the staff area.
    await staffLogout();
    navigate("/staff/login", { replace: true });
  };

  const isActive = (itemTitle) => itemTitle === title;

  const NavList = (variant) => (
    <ul
      data-staff-nav={variant}
      onScroll={(event) => {
        try {
          sessionStorage.setItem(`staff-sidebar-scroll-${variant}`, String(event.currentTarget.scrollTop));
        } catch {
          // Session storage is optional; scrolling still works normally.
        }
      }}
      className="staff-nav-scroll flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overscroll-contain px-2 py-1"
    >
      {STAFF_NAV_ITEMS.map((item) => {
        const active = isActive(item.title);
        return (
          <li key={item.title}>
            <a
              onClick={() => navigate(item.path)}
              className={`group flex no-underline cursor-pointer items-center gap-3 rounded-xl border-l-4 px-1 py-2.5 text-sm transition-colors ${
                active
                  ? "bg-white/10 font-semibold text-white"
                  : "border-transparent text-slate-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              <FontAwesomeIcon
                icon={item.icon}
                className={`w-4 shrink-0 ${active ? "text-white" : "text-slate-400 group-hover:text-white/50"}`}
              />
              <span className="flex-1">{item.title}</span>
              {item.title === "Content Review" && staffPendingCount > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold leading-none text-white">
                  {staffPendingCount > 99 ? "99+" : staffPendingCount}
                </span>
              )}
            </a>
          </li>
        );
      })}
    </ul>
  );

  const SidebarBrand = () => (
    <div className="flex shrink-0 flex-col items-start gap-3 border-b border-white/10 px-4 pb-6 pt-8">
      <div className="flex items-center gap-2">
        <img src="/images/logo.png" alt="Fillop" className="h-9 w-9 rounded-lg object-contain" />
        <span className="text-2xl text-white">Fillop</span>
      </div>
      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-400/15 px-3 py-1 text-xs font-semibold tracking-wide text-blue-300">
        <FontAwesomeIcon icon={faUser} className="text-[11px]" />
        Staff
      </span>
    </div>
  );

  const LogoutButton = () => (
    <div className="shrink-0 border-t border-white/10 px-1">
      <button
        onClick={handleLogout}
        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-red-400"
      >
        <FontAwesomeIcon icon={faSignOutAlt} className="w-4 shrink-0" />
        <span>Logout</span>
      </button>
    </div>
  );

  return (
    <>
      {/* Mobile top bar trigger */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed left-4 top-4 z-40 flex h-10 w-10 items-center justify-center rounded-lg bg-[#16213A] text-white shadow-md md:hidden"
        aria-label="Open menu"
      >
        <FontAwesomeIcon icon={faBars} />
      </button>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`fixed inset-y-0 left-0 z-50 flex w-72 -translate-x-full transform flex-col bg-[#16213A] transition-transform duration-300 ease-in-out md:hidden ${
          isOpen ? "translate-x-0" : ""
        }`}
      >
        <button
          onClick={() => setIsOpen(false)}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-white/5 hover:text-white"
          aria-label="Close menu"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <SidebarBrand />
        {NavList("mobile")}
        <LogoutButton />
      </div>

      {/* Desktop sidebar */}
      <div className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col bg-[#16213A] md:flex">
        <SidebarBrand />
        {NavList("desktop")}
        <LogoutButton />
      </div>

      {/* Header */}
      <div className="sticky top-0 z-20 bg-white md:pl-64">
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-2 md:px-8">
          <div className="ml-12 flex w-full max-w-sm items-center gap-2 rounded-full bg-slate-100 px-4 py-2 md:ml-0">
            <FontAwesomeIcon icon={faSearch} className="text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
            />
          </div>

          <div className="flex items-center gap-4">
            <a
              onClick={() => navigate("/staff/notifications")}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-[#16213A]"
            >
              <FontAwesomeIcon icon={faBell} />
            </a>

            <span className="hidden h-6 w-px bg-slate-200 sm:block" />

            <a
              onClick={() => navigate("/staff/account/settings")}
              className="flex cursor-pointer items-center gap-2.5"
            >
              {name ? (
                <span className="hidden text-sm text-slate-600 sm:inline">
                  Hi, <span className="font-semibold no-underline text-slate-900">{name}</span>
                </span>
              ) : null}

              {(uimage || isloaded) && (
                <div className="relative h-9 w-9 shrink-0">
                  <img
                    src={imageUrl(uimage) || "/images/default_profile.png"}
                    alt="Profile"
                    className="h-9 w-9 rounded-full border-2 border-white object-cover shadow"
                  />
                </div>
              )}
            </a>
          </div>
        </div>

        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-4 pb-2 pt-2 md:px-8">
          <h1 className="text-xl font-bold tracking-tight text-slate-900">{title}</h1>
          {subTitle !== "" && <h2 className="text-sm font-medium text-slate-400">/ {subTitle}</h2>}
          {context !== "" && <p className="w-full text-sm text-slate-500">{context}</p>}
        </div>
      </div>
    </>
  );
};

export default StaffNavBar;
